import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import styled from "styled-components";
import { PageHeaderBar, PageHeaderTitle } from "../components/PageHeader";
import { Row, Col } from "react-bootstrap";
import moment from "moment-timezone";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { isValidNumber } from "libphonenumber-js";
import Loader from "../components/Loader";
import Messages from "../components/Messages";
import { updateRestaurant, createRestaurant, destroyItems } from "../actions/restaurantActions";
import { getDialCode, getCountryCode } from "../components/CountryCode";
import { Container, StyledLabel, ModernButton } from "../components/DefaultImports";
import PhoneField from "../components/PhoneField";
import { useNavigate } from "react-router-dom";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { toast, ToastContainer } from "react-toastify";
import { LuCopy, LuPlus, LuCode2, LuTrash2 } from "react-icons/lu";
import { ConfirmationModal } from "../components/ConfirmationModal";
import { OverviewButton } from "../components/overview/overviewStyles";

/* global google */

const BOTTOM_PADDING = 20;
const DEFAULT_MAP_CENTER = {
  lat: 38.7223,
  lng: -9.1393
};
const FIELD_HEIGHT = 44;
const PageShell = styled.div`
  width: 100%;
  min-width: 0;
`;
const PageScroll = styled.div`
  width: 100%;
  box-sizing: border-box;
  overflow-y: ${({
  height
}) => height > 260 ? "auto" : "visible"};
  overflow-x: hidden;
  height: ${({
  height
}) => height > 260 ? `${height}px` : "auto"};
  padding: 0 0 18px;

  scrollbar-width: thin;
  scrollbar-color: #ccc transparent;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 8px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #999;
  }

  @media (max-width: 768px) {
    padding-right: 2px;
  }
`;
const Form = styled.form`
  width: 100%;
  max-width: none;
  margin: 0;
  padding: 0 0 20px;
  box-sizing: border-box;
`;
const SectionCard = styled.div`
  width: 100%;
  background: #ffffff;
  border-radius: 0;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 0;
  }
`;
const FieldBlock = styled.div`
  width: 100%;
  min-width: 0;
  margin: 0 0 14px;
`;
const FieldLabel = styled(StyledLabel)`
  display: block;
  margin: 0 0 6px;
  line-height: 1.2;
  font-size: 11px;
`;
/* These were on their own palette: #ddd borders, #333 text and a #de8c3a
   focus ring, which is a different orange from the app's. They use the same
   tokens as every other field now. */
const sharedFieldStyles = `
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  border-radius: var(--app-radius-sm, 8px);
  background-color: var(--app-surface, #ffffff);
  color: var(--app-text, #4b3335);
  font-size: 14px;
  line-height: 1.35;
  transition: all 0.2s ease;

  &::placeholder {
    color: #aaa;
  }

  &:focus {
    background-color: #fff;
    outline: none;
  }
`;
const StyledInput = styled.input`
  ${sharedFieldStyles}
  height: 42px;
  padding: 0 14px;
  border: 1px solid ${({
  $invalid
}) => $invalid ? "var(--app-danger, #c94b42)" : "var(--app-border, #ebe8e6)"};

  &:focus {
    border-color: ${({
  $invalid
}) => $invalid ? "var(--app-danger, #c94b42)" : "var(--app-orange, #ee754c)"};
    box-shadow: ${({
  $invalid
}) => $invalid ? "0 0 0 3px rgba(201, 75, 66, 0.18)" : "var(--app-focus, 0 0 0 3px rgba(238, 117, 76, 0.2))"};
  }
`;
const StyledTextArea = styled.textarea`
  ${sharedFieldStyles}
  min-height: 88px;
  resize: vertical;
  padding: 12px 14px;
  border: 1px solid ${({
  $invalid
}) => $invalid ? "var(--app-danger, #c94b42)" : "var(--app-border, #ebe8e6)"};

  &:focus {
    border-color: ${({
  $invalid
}) => $invalid ? "var(--app-danger, #c94b42)" : "var(--app-orange, #ee754c)"};
    box-shadow: ${({
  $invalid
}) => $invalid ? "0 0 0 3px rgba(201, 75, 66, 0.18)" : "var(--app-focus, 0 0 0 3px rgba(238, 117, 76, 0.2))"};
  }
`;
const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(360px, 1.1fr);
  gap: 20px;
  align-items: stretch;
  width: 100%;
  min-width: 0;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;
const LeftColumn = styled.div`
  min-width: 0;
`;
const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  width: 100%;

  ${FieldBlock} {
    margin-bottom: 0;
  }

  @media (max-width: 576px) {
    grid-template-columns: 1fr;

    ${FieldBlock} {
      margin-bottom: 0;
    }
  }
`;
const PhoneFieldFrame = styled.div`
  width: 100%;
  min-width: 0;

  .react-tel-input {
    width: 100%;
  }

  .react-tel-input .form-control {
    width: 100% !important;
    min-height: ${FIELD_HEIGHT}px !important;
    height: ${FIELD_HEIGHT}px !important;
    border-radius: 10px !important;
    background-color: #fafafa !important;
    border-color: ${({
  $invalid
}) => $invalid ? "#dc3545" : "#ddd"} !important;
    font-size: 15px !important;
    box-sizing: border-box !important;
  }

  .react-tel-input .flag-dropdown {
    border-radius: 10px 0 0 10px !important;
    border-color: ${({
  $invalid
}) => $invalid ? "#dc3545" : "#ddd"} !important;
    background-color: #fafafa !important;
  }

  input {
    box-sizing: border-box;
  }
`;
const MapColumn = styled.div`
  min-width: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
`;
const MapContainer = styled.div`
  width: 100%;
  min-width: 0;
  flex: 1;
  min-height: 252px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid rgba(153, 176, 173, 0.28);
  background: #f7f7f7;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);

  @media (max-width: 900px) {
    height: 300px;
    flex: none;
  }

  @media (max-width: 576px) {
    height: 260px;
  }
`;
const MapStateBox = styled.div`
  width: 100%;
  height: 100%;
  min-height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 18px;
  color: #7f8f8c;
  font-size: 14px;
  background: #f7f7f7;
`;
const SaveRow = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  margin-top: 14px;
`;
/* What the restaurant home carried besides the plan: the booking link, a
   customer booking, the widget, and deleting the restaurant. */
const ShortcutsCard = styled.section`
  width: 100%;
  margin: 0 0 22px;
  padding: 0 0 20px;
  border-bottom: 1px solid #edf0ee;
`;
const ShortcutsTitle = styled.h2`
  font-size: 13px;
  font-weight: 650;
  color: #344b43;
  margin: 0 0 4px;
`;
const ShortcutsText = styled.p`
  font-size: 12px;
  color: #788580;
  margin: 0 0 12px;
`;
const ShortcutsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
`;
const DangerRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px dashed #f0dcd6;

  p {
    margin: 0;
    font-size: 12px;
    color: #9a6a5f;
    max-width: 520px;
  }
`;
const DeleteNameInput = styled.input`
  display: block;
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${({ $invalid }) => $invalid ? "#d45b45" : "#dce4df"};
  border-radius: 8px;
  margin-top: 8px;
`;
const InformationHeader = PageHeaderBar;
const InformationTitle = PageHeaderTitle;
const ErrorBox = styled.div`
  width: 100%;
  margin: 0 0 14px;
  padding: 12px 14px;
  border-radius: 10px;
  background: rgba(220, 53, 69, 0.08);
  border: 1px solid rgba(220, 53, 69, 0.25);
  color: #b02a37;
  font-size: 14px;
  line-height: 1.4;
`;
const CleanRow = styled(Row)`
  margin-left: 0 !important;
  margin-right: 0 !important;
`;
const CleanCol = styled(Col)`
  padding-left: 0 !important;
  padding-right: 0 !important;
  min-width: 0;
`;
const getTimezone = () => {
  if (window.Intl && window.Intl.DateTimeFormat) {
    const options = new window.Intl.DateTimeFormat().resolvedOptions();
    return options && options.timeZone ? options.timeZone : "";
  }
  return "";
};
function RestaurantSettingsUpdate({
  creating_restaurant = "no",
  handleNextStep = false,
  restaurantCreated = false,
  setCreateRestaurantToFalse = false,
  steps = false
}) {
  const {
    t
  } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { managerInfo } = useSelector(state => state.crudManager);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [enteredName, setEnteredName] = useState("");
  const [wrongName, setWrongName] = useState(false);
  const [restaurantId, setRestaurantId] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("ok_status");
  const [restaurantName, setRestaurantName] = useState("");
  const [address, setAddress] = useState("");
  const [postalCode, setPostalCode] = useState(false);
  const [locality, setLocality] = useState("");
  const [longitudeField, setLongitudeField] = useState(0.0);
  const [latitudeField, setLatitudeField] = useState(0.0);
  const [viewDistance, setViewDistance] = useState(18);
  const [numberOfChairs, setNumberOfChairs] = useState(0);
  const [numberOfBabyChairs, setNumberOfBabyChairs] = useState("");
  const [limitPeopleReservation, setLimitPeopleReservation] = useState("");
  const [limitBabyChairsReservation, setLimitBabyChairsReservation] = useState("");
  const [autoAcceptLimit, setAutoAcceptLimit] = useState("");
  const [eachTime, setEachTime] = useState("00:15");
  const [acceptPeoplePerEachTime, setAcceptPeoplePerEachTime] = useState("");
  const [timezone, setTimezone] = useState(getTimezone());
  const [autoAcceptTimeLimit, setAutoAcceptTimeLimit] = useState("");
  const [messageReminderTime, setMessageReminderTime] = useState("");
  const [autoSetTables, setAutoSetTables] = useState(null);
  const [timeToCancelledWarning, setTimeToCancelledWarning] = useState("");
  const [countryCode, setCountryCode] = useState("pt");
  const [countryCodeNumber, setCountryCodeNumber] = useState("351");
  const [phoneWithoutCountryCode, setPhoneWithoutCountryCode] = useState("");
  const [restaurant_description, setRestaurant_description] = useState("");
  const [reconfirmation_duration, setReconfirmation_duration] = useState("");
  const [reconfirmation_time_before_date, setReconfirmation_time_before_date] = useState("");
  const {
    isLoaded: isGoogleLoaded,
    loadError
  } = useJsApiLoader({
    googleMapsApiKey: "AIzaSyDtPooGWkj4z_ExlO9ggRMwwI9QiFZuZTI"
  });
  const [marker, setMarker] = useState(false);
  const [allowGeocode, setAllowGeocode] = useState(false);
  const [mapCenter, setMapCenter] = useState(DEFAULT_MAP_CENTER);
  const [isPhoneValid, setIsPhoneValid] = useState(true);
  const pageRef = useRef(null);
  const [pageHeight, setPageHeight] = useState(0);
  const [invalidFields, setInvalidFields] = useState(new Set());
  useLayoutEffect(() => {
    if (!pageRef.current) return;
    const updateHeight = () => {
      if (!pageRef.current) return;
      const top = pageRef.current.getBoundingClientRect().top;
      const height = window.innerHeight - top - BOTTOM_PADDING;
      setPageHeight(Math.max(0, height));
    };
    updateHeight();
    window.addEventListener("resize", updateHeight);
    window.addEventListener("orientationchange", updateHeight);
    const ro = new ResizeObserver(() => updateHeight());
    ro.observe(document.body);
    const raf = requestAnimationFrame(updateHeight);
    return () => {
      window.removeEventListener("resize", updateHeight);
      window.removeEventListener("orientationchange", updateHeight);
      ro.disconnect();
      cancelAnimationFrame(raf);
    };
  }, []);
  useEffect(() => {
    const timer = setTimeout(() => {
      setAllowGeocode(true);
    }, 3000);
    return () => clearTimeout(timer);
  }, []);
  useEffect(() => {
    if (!allowGeocode || !isGoogleLoaded) return;
    const fullAddressQuery = [address, locality, postalCode].filter(Boolean).join(", ");
    if (!fullAddressQuery) return;
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({
      address: fullAddressQuery
    }, (results, status) => {
      if (status === "OK" && results.length > 0) {
        const location = results[0].geometry.location;
        const nextCenter = {
          lat: location.lat(),
          lng: location.lng()
        };
        setLatitudeField(nextCenter.lat);
        setLongitudeField(nextCenter.lng);
        setMarker(nextCenter);
        setMapCenter(nextCenter);
      } else {
        console.error("Geocode failed:", status);
      }
    });
  }, [address, locality, postalCode, isGoogleLoaded, allowGeocode]);
  const detectCountryCode = () => {
    const userCountryCode = navigator.language.split("-")[1];
    return userCountryCode ? userCountryCode.toLowerCase() : "us";
  };
  const handleOnZoomChanged = React.useCallback(map => {
    const zoomLevel = map.getZoom();
    setViewDistance(zoomLevel);
  }, []);
  useEffect(() => {
    if (countryCodeNumber === "") {
      const detectedCountryCode = detectCountryCode();
      setCountryCode(detectedCountryCode);
      setCountryCodeNumber(getDialCode(detectedCountryCode));
    } else {
      setCountryCode(getCountryCode(countryCodeNumber));
    }
  }, [countryCodeNumber, countryCode]);
  const onMapClick = React.useCallback(event => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    setMarker({
      lat,
      lng
    });
    setLatitudeField(lat);
    setLongitudeField(lng);
    setMapCenter({
      lat,
      lng
    });
  }, []);
  const handleOnLoad = map => {
    if (isGoogleLoaded) {
      google.maps.event.trigger(map, "resize");
      map.addListener("zoom_changed", () => handleOnZoomChanged(map));
    }
  };
  const restaurantDetails = useSelector(state => state.restaurantDetails);
  const {
    restaurants,
    isLoading,
    error
  } = restaurantDetails;
  useEffect(() => {
    if (restaurants) {
      setRestaurantId(restaurants.id);
    }
  }, [restaurants]);
  useEffect(() => {
    if (restaurants && restaurants.id) {
      const parsedLat = parseFloat(restaurants.latitude) || 0;
      const parsedLng = parseFloat(restaurants.longitude) || 0;
      const hasValidCoordinates = parsedLat !== 0 || parsedLng !== 0;
      const nextMapCenter = hasValidCoordinates ? {
        lat: parsedLat,
        lng: parsedLng
      } : DEFAULT_MAP_CENTER;
      setRestaurantName(restaurants.restaurant_name || "");
      setAddress(restaurants.adress || "");
      setPostalCode(restaurants.postal_code || "");
      setLocality(restaurants.locality || "");
      setLatitudeField(parsedLat);
      setLongitudeField(parsedLng);
      setViewDistance(restaurants.view_distance || 18);
      setMarker(hasValidCoordinates ? nextMapCenter : false);
      setMapCenter(nextMapCenter);
      setNumberOfBabyChairs(restaurants.number_of_babychairs || 0);
      setLimitPeopleReservation(restaurants.limit_people_reservation || 0);
      setLimitBabyChairsReservation(restaurants.limit_baby_chairs_reservation || 0);
      setEachTime(restaurants.each_time || "00:15");
      setAutoAcceptLimit(restaurants.auto_accept_limit || "");
      setAcceptPeoplePerEachTime(restaurants.accept_people_per_each_time || "");
      setTimezone(restaurants.timezone || getTimezone());
      setAutoAcceptTimeLimit(restaurants.only_auto_accept_before_this_time || "");
      setMessageReminderTime(restaurants.send_message_reminder_to_customers || "");
      setAutoSetTables(restaurants.auto_set_tables || null);
      setTimeToCancelledWarning(restaurants.time_to_cancelled_warning || "");
      setPhoneWithoutCountryCode(restaurants.phone_number || "");
      setCountryCodeNumber(restaurants.country_code || "351");
      setRestaurant_description(restaurants.restaurant_description || "");
      setReconfirmation_duration(restaurants.reconfirmation_duration || "");
      setReconfirmation_time_before_date(restaurants.reconfirmation_time_before_date || "");
    }
  }, [restaurants]);
  function checkEmptyStates() {
    const invalid = new Set();
    const emptyStates = [];
    if (restaurantName === "") {
      emptyStates.push(t("ui.i18nFinalCleanup.restaurantName"));
      invalid.add("restaurant_name");
    }
    if (!address) {
      emptyStates.push(t("ui.i18nFinalCleanup.address"));
      invalid.add("adress");
    }
    if (numberOfBabyChairs === "") {
      emptyStates.push(t("ui.i18nFinalCleanup.numberOfBabyChairs"));
      invalid.add("number_of_babychairs");
    }
    if (!limitPeopleReservation) {
      emptyStates.push(t("ui.i18nFinalCleanup.peoplePerReservationLimit"));
      invalid.add("limit_people_reservation");
    }
    if (!limitBabyChairsReservation) {
      emptyStates.push(t("ui.i18nFinalCleanup.babyChairsPerReservationLimit"));
      invalid.add("limit_baby_chairs_reservation");
    }
    if (!eachTime) {
      emptyStates.push(t("ui.i18nFinalCleanup.timeInterval"));
      invalid.add("each_time");
    }
    if (!acceptPeoplePerEachTime) {
      emptyStates.push(t("ui.i18nFinalCleanup.peoplePerSlotLimit"));
      invalid.add("accept_people_per_each_time");
    }
    if (phoneWithoutCountryCode && !isValidNumber("+" + countryCodeNumber + phoneWithoutCountryCode)) {
      emptyStates.push(t("ui.i18nFinalCleanup.invalidPhone"));
      invalid.add("phone_number");
    }
    if (!postalCode) {
      emptyStates.push(t("ui.i18nFinalCleanup.postalCode"));
      invalid.add("postalCode");
    }
    if (!locality) {
      emptyStates.push(t("ui.i18nFinalCleanup.locality"));
      invalid.add("locality");
    }
    setInvalidFields(invalid);
    if (emptyStates.length > 0) {
      return t("ui.i18nFinalCleanup.invalidFields", { fields: emptyStates.join(", ") });
    }
    return "ok_status";
  }
  function checkForErrorsAndCreateRestaurant() {
    const status_error = checkEmptyStates();
    setErrorMessage(status_error);
    if (status_error === "ok_status") {
      setShowError(false);
      const updatedRestaurantParams = {
        restaurant_name: restaurantName,
        adress: address,
        postal_code: postalCode,
        locality: locality,
        latitude: latitudeField,
        longitude: longitudeField,
        view_distance: viewDistance,
        number_of_chairs: numberOfChairs,
        number_of_babychairs: numberOfBabyChairs,
        limit_people_reservation: limitPeopleReservation,
        limit_baby_chairs_reservation: limitBabyChairsReservation,
        auto_accept_limit: autoAcceptLimit,
        accept_people_per_each_time: acceptPeoplePerEachTime,
        each_time: eachTime,
        timezone: timezone,
        only_auto_accept_before_this_time: autoAcceptTimeLimit,
        send_message_reminder_to_customers: messageReminderTime,
        auto_set_tables: autoSetTables,
        time_to_cancelled_warning: timeToCancelledWarning,
        country_code: countryCodeNumber,
        phone_number: phoneWithoutCountryCode,
        restaurant_description: restaurant_description,
        reconfirmation_duration: reconfirmation_duration,
        reconfirmation_time_before_date: reconfirmation_time_before_date
      };
      if (creating_restaurant === "no") {
        dispatch(updateRestaurant(restaurantId, updatedRestaurantParams));
        if (steps) {
          setCreateRestaurantToFalse();
          handleNextStep();
        }
      } else {
        dispatch(createRestaurant(updatedRestaurantParams));
        setCreateRestaurantToFalse();
        handleNextStep();
      }
    } else {
      setShowError(true);
    }
  }
  const handleSubmit = e => {
    e.preventDefault();
    checkForErrorsAndCreateRestaurant();
  };
  useEffect(() => {
    if (restaurantCreated === true) {
      checkForErrorsAndCreateRestaurant();
    }
  }, [restaurantCreated]);
  const handleChange = e => {
    const {
      name,
      value
    } = e.target;
    switch (name) {
      case "restaurant_name":
        setRestaurantName(value);
        break;
      case "adress":
        setAddress(value);
        break;
      case "postalCode":
        setPostalCode(value);
        break;
      case "locality":
        setLocality(value);
        break;
      case "number_of_chairs":
        setNumberOfChairs(value);
        break;
      case "number_of_babychairs":
        setNumberOfBabyChairs(value);
        break;
      case "limit_people_reservation":
        setLimitPeopleReservation(value);
        break;
      case "limit_baby_chairs_reservation":
        setLimitBabyChairsReservation(value);
        break;
      case "auto_accept_limit":
        setAutoAcceptLimit(value);
        break;
      case "accept_people_per_each_time":
        setAcceptPeoplePerEachTime(value);
        break;
      case "each_time":
        setEachTime(value);
        break;
      default:
        break;
    }
  };
  const handleMarkerDragEnd = React.useCallback(event => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    setMarker({
      lat,
      lng
    });
    setLatitudeField(lat);
    setLongitudeField(lng);
    setMapCenter({
      lat,
      lng
    });
  }, []);
  const activeMapCenter = marker || mapCenter || DEFAULT_MAP_CENTER;
  // Only for an existing restaurant, not while one is being created.
  const isEditingExisting = creating_restaurant === "no" && steps === false && Boolean(restaurants?.id);
  const planIsActive = ["active", "trialling", "trialing"].includes(restaurants?.subscription_status);
  const bookingLink = restaurants?.id ? `${window.location.origin}/#/reservations/${restaurants.id}/client/no_id` : "";
  const askDelete = () => {
    setDeleteTarget(restaurants);
    setEnteredName("");
    setWrongName(false);
  };
  const confirmDelete = () => {
    if (!deleteTarget || enteredName !== deleteTarget.restaurant_name) {
      setWrongName(true);
      return;
    }
    dispatch(destroyItems(deleteTarget.id, "restaurants"));
    setDeleteTarget(null);
    navigate("/");
  };
  return <Container>
      <PageShell>
        <CleanRow>
          <CleanCol sm={12} md={12} xl={12}>
            <InformationHeader data-setup-tour="information">
              <InformationTitle>{t("ui.screens.restaurantInfoUpdate.informacoes")}</InformationTitle>
            </InformationHeader>

            {isLoading ? <Loader /> : error ? <Messages variant="error">{error}</Messages> : <PageScroll ref={pageRef} height={pageHeight}>
                {isEditingExisting && <ShortcutsCard aria-labelledby="restaurant-shortcuts-title">
                    <ShortcutsTitle id="restaurant-shortcuts-title">{t("ui.screens.restaurantInfoUpdate.shortcutsTitle")}</ShortcutsTitle>
                    <ShortcutsText>{t("ui.screens.restaurantInfoUpdate.shortcutsText")}</ShortcutsText>
                    {planIsActive && <ShortcutsRow>
                        <CopyToClipboard text={bookingLink} onCopy={() => toast(t("ui.i18nMessages.linkCopied"), { autoClose: 1000 })}>
                          <OverviewButton type="button"><LuCopy />{t("restaurantOverview.copy")}</OverviewButton>
                        </CopyToClipboard>
                        <OverviewButton type="button" onClick={() => navigate(`/reservations/${restaurants.id}/client/no_id`)}><LuPlus />{t("restaurantOverview.customerReservation")}</OverviewButton>
                        <OverviewButton type="button" onClick={() => navigate(`/restaurant_widget/${restaurants.id}`)}><LuCode2 />{t("ui.screens.homeScreen.widget")}</OverviewButton>
                      </ShortcutsRow>}
                    {!managerInfo && <DangerRow>
                        <p>{t("ui.screens.restaurantInfoUpdate.deleteHint")}</p>
                        <OverviewButton type="button" $danger onClick={askDelete}><LuTrash2 />{t("restaurantOverview.remove")}</OverviewButton>
                      </DangerRow>}
                  </ShortcutsCard>}

                <Form onSubmit={handleSubmit}>
                  {showError && errorMessage !== "ok_status" && <ErrorBox>{errorMessage}</ErrorBox>}

                  <SectionCard>
                    <FieldBlock>
                      <FieldLabel htmlFor="restaurant_name">{t("ui.screens.restaurantInfoUpdate.nomeDoRestaurante")}</FieldLabel>
                      <StyledInput type="text" id="restaurant_name" name="restaurant_name" placeholder={t("ui.screens.restaurantInfoUpdate.nomeDoRestaurante2")} value={restaurantName} onChange={handleChange} $invalid={invalidFields.has("restaurant_name")} />
                    </FieldBlock>

                    <FieldBlock>
                      <FieldLabel htmlFor="restaurant_description">{t("ui.screens.restaurantInfoUpdate.descricao")}</FieldLabel>
                      <StyledTextArea id="restaurant_description" rows={4} value={restaurant_description} onChange={e => setRestaurant_description(e.target.value)} placeholder={t("ui.screens.restaurantInfoUpdate.escrevaUmaBreveDescricaoDoRestaurante")} />
                    </FieldBlock>

                    <DetailsGrid>
                      <LeftColumn>
                        <FieldBlock>
                          <FieldLabel>{t("ui.screens.restaurantInfoUpdate.telemovel")}</FieldLabel>
                          <PhoneFieldFrame $invalid={invalidFields.has("phone_number") || !isPhoneValid}>
                            <PhoneField countryCodeNumber={countryCodeNumber} setCountryCodeNumber={setCountryCodeNumber} phoneWithoutCountryCode={phoneWithoutCountryCode} setPhoneWithoutCountryCode={setPhoneWithoutCountryCode} isPhoneValid={isPhoneValid} setIsPhoneValid={setIsPhoneValid} initialFullPhone={`+${countryCodeNumber}${phoneWithoutCountryCode}`} />
                          </PhoneFieldFrame>
                        </FieldBlock>

                        <FieldBlock>
                          <FieldLabel htmlFor="adress">{t("ui.screens.restaurantInfoUpdate.morada")}</FieldLabel>
                          <StyledInput type="text" id="adress" name="adress" value={address} onChange={handleChange} $invalid={invalidFields.has("adress")} />
                        </FieldBlock>

                        <TwoColumnGrid>
                          <FieldBlock>
                            <FieldLabel htmlFor="postalCode">{t("ui.screens.restaurantInfoUpdate.codigoPostal")}</FieldLabel>
                            <StyledInput type="text" id="postalCode" name="postalCode" value={postalCode} onChange={handleChange} $invalid={invalidFields.has("postalCode")} />
                          </FieldBlock>

                          <FieldBlock>
                            <FieldLabel htmlFor="locality">{t("ui.screens.restaurantInfoUpdate.localidade")}</FieldLabel>
                            <StyledInput type="text" id="locality" name="locality" value={locality} onChange={handleChange} $invalid={invalidFields.has("locality")} />
                          </FieldBlock>
                        </TwoColumnGrid>
                      </LeftColumn>

                      <MapColumn>
                        <FieldLabel>{t("ui.screens.restaurantInfoUpdate.localizacaoNoMapa")}</FieldLabel>
                        <MapContainer>
                          {loadError ? <MapStateBox>{t("ui.screens.restaurantInfoUpdate.naoFoiPossivelCarregarOMapa")}</MapStateBox> : !isGoogleLoaded ? <MapStateBox>{t("ui.screens.restaurantInfoUpdate.aCarregarMapa")}</MapStateBox> : <GoogleMap mapContainerStyle={{
                        width: "100%",
                        height: "100%"
                      }} center={activeMapCenter} zoom={viewDistance || 18} onClick={onMapClick} onLoad={handleOnLoad} options={{
                        streetViewControl: false,
                        mapTypeControl: false,
                        fullscreenControl: false,
                        zoomControl: true
                      }}>
                              {marker && <Marker position={{
                          lat: marker.lat,
                          lng: marker.lng
                        }} draggable={true} onDragEnd={handleMarkerDragEnd} />}
                            </GoogleMap>}
                        </MapContainer>
                      </MapColumn>
                    </DetailsGrid>

                    {steps === false && <SaveRow>
                        <ModernButton color="#EE754C" fontSize="14px" type="submit">{t("ui.screens.restaurantInfoUpdate.guardar")}</ModernButton>
                      </SaveRow>}
                  </SectionCard>
                </Form>
              </PageScroll>}
          </CleanCol>
        </CleanRow>
      </PageShell>
      <ToastContainer />
      <ConfirmationModal show={Boolean(deleteTarget)} onHide={() => setDeleteTarget(null)} onConfirm={confirmDelete} title={t("ui.screens.homeScreen.apagarRestaurante")} body={<>
            <p>{t("ui.screens.homeScreen.temACertezaQuePretendeApagar")}</p>
            <p>{t("ui.screens.homeScreen.atencaoSeApagarORestauranteIra")}{t("ui.i18nCleanup2.reservasClientesInformacaoDefinicoesEtc")}!</p>
            <label htmlFor="restaurantNameInput">{t("ui.screens.homeScreen.escreva")}<strong>“{deleteTarget?.restaurant_name}”</strong>{t("ui.screens.homeScreen.paraContinuar")}</label>
            <DeleteNameInput id="restaurantNameInput" value={enteredName} $invalid={wrongName} aria-invalid={wrongName} aria-describedby={wrongName ? "delete-name-error" : undefined} onChange={event => {
          setEnteredName(event.target.value);
          setWrongName(false);
        }} />
            {wrongName && <p id="delete-name-error" role="alert">{t("restaurantOverview.wrongName")}</p>}
          </>} confirmLabel={t("ui.components.restaurantSchedule.openClosedExceptions.apagar")} cancelLabel={t("common.cancel")} confirmColor="#ee754c" cancelColor="#99b0ad" />
    </Container>;
}
export default RestaurantSettingsUpdate;
